import {
  FC,
  useState,
  createContext,
  ReactNode,
  useContext,
  useCallback,
  useMemo,
  useEffect,
} from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';
import { lightTheme, darkTheme } from 'theme/theme';
import { Theme, ThemeProvider as MuiThemeProvider } from '@mui/material';
import createSxStyles, {
  CreateSxStyles,
} from 'helpers/styleHelpers/createSxStyles';
import mergeSxStyles, {
  MergeSxStyles,
} from 'helpers/styleHelpers/mergeSxStyles';
import createEmotionCssStyles, {
  CreateEmotionCssStyles,
} from 'helpers/styleHelpers/createEmotionCssStyles';
import themeConstants from 'theme/themeConstants';
import { ThemeProvider as EmotionThemeProvider } from '@emotion/react';
import createCssStyles, {
  CreateCssStyles,
} from 'helpers/styleHelpers/createCssStyles';

interface ThemeContextProps {
  themeMode: 'light' | 'dark';
  theme: Theme;
  themeConstants: typeof themeConstants;
  createSxStyles: CreateSxStyles;
  mergeSxStyles: MergeSxStyles;
  createEmotionCssStyles: CreateEmotionCssStyles;
  createCssStyles: CreateCssStyles;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextProps>({
  themeMode: 'light',
  theme: lightTheme,
  themeConstants,
  createSxStyles: () => {
    throw new Error(
      "createSxStyles cannot be called outside of the ThemeProvider's context.",
    );
  },
  mergeSxStyles: () => {
    throw new Error(
      "mergeSxStyles cannot be called outside of the ThemeProvider's context.",
    );
  },
  createEmotionCssStyles: () => {
    throw new Error(
      "createEmotionCssStyles cannot be called outside of the ThemeProvider's context.",
    );
  },
  createCssStyles: () => {
    throw new Error(
      "createCssStyles cannot be called outside of the ThemeProvider's context.",
    );
  },
  toggleTheme: () => {
    throw new Error(
      "toggleTheme cannot be called outside of the ThemeProvider's context.",
    );
  },
});

interface ThemeProviderProps {
  children: ReactNode;
}

const ThemeProvider: FC<ThemeProviderProps> = ({ children }) => {
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
  const defaultTheme = useMemo<ThemeContextProps['themeMode']>(
    () => (prefersDarkMode ? 'dark' : 'light'),
    [prefersDarkMode],
  );

  const themeModeSetting = useMemo(() => {
    const saved = localStorage.getItem('themeMode');
    if (saved === null) {
      return defaultTheme;
    }

    return saved === 'dark' ? 'dark' : 'light';
  }, [defaultTheme]);

  const [themeMode, setThemeMode] =
    useState<ThemeContextProps['themeMode']>(themeModeSetting);

  const toggleTheme = useCallback(() => {
    setThemeMode((prevValue) => (prevValue === 'light' ? 'dark' : 'light'));
  }, []);

  useEffect(() => {
    localStorage.setItem('themeMode', themeMode);
  }, [themeMode]);

  const providerValue = useMemo(
    () => ({
      themeMode,
      theme: themeMode === 'light' ? lightTheme : darkTheme,
      themeConstants,
      createSxStyles,
      mergeSxStyles,
      createEmotionCssStyles,
      createCssStyles,
      toggleTheme,
    }),
    [themeMode, toggleTheme],
  );

  return (
    <ThemeContext.Provider value={providerValue}>
      <MuiThemeProvider theme={providerValue.theme}>
        <EmotionThemeProvider theme={providerValue.theme}>
          {children}
        </EmotionThemeProvider>
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

export default ThemeProvider;

export const useThemeContext = () => useContext(ThemeContext);
